// EXTERNAL DEPENDENCIES
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

// HELPERS
import { makeRequest } from '../api'

// DATA
import serverData from '../utils/server-data'

// TYPES
import { AxiosError } from 'axios'

/**
 * Hook to make axios request
 */
export function useMakeRequest<T = unknown, E = unknown>(
  url: string,
  additionalOptions = {},
) {
  return useQuery<T, AxiosError<E>, T, [url: string]>({
    queryKey: [url],
    queryFn: () => makeRequest(url),
    refetchOnWindowFocus: false,
    ...additionalOptions,
  })
}

/**
 * Hook intended to make mutative (POST/PATCH/PUT) axios request
 */
export function useMutativeRequest<T, E>(
  url: string,
  axiosOptions = {},
  additionalOptions = {},
) {
  return useMutation<
    T,
    AxiosError<E>,
    T,
    [url: string, { headers: object; data: object }?]
  >({
    mutationFn: (requestBody: unknown) => {
      return makeRequest(url, {
        method: 'POST',
        ...axiosOptions,
        data: { ...(requestBody as object), _csrf: serverData?._csrf },
      })
    },
    ...additionalOptions,
  })
}

/**
 * Fetches the custom query key provided
 */
export function useStaleRefetching() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.refetchQueries({ stale: true })
  }
}

/**
 * Check if any mutations are in progress
 */
export function useMutationCheck() {
  return useIsMutating() > 0
}
